import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const RISCO_COLORS = {
  alto: "border-red-500 bg-red-50 text-red-900",
  medio: "border-amber-500 bg-amber-50 text-amber-900",
  baixo: "border-yellow-500 bg-yellow-50 text-yellow-900",
  nenhum: "border-green-500 bg-green-50 text-green-900",
};

export default function Chat() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [montadores, setMontadores] = useState([]);
  const [conversaCom, setConversaCom] = useState(null); // {user_id, role, nome}
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState("");
  const [tab, setTab] = useState("conversas"); // conversas | montadores
  const [aiLoading, setAiLoading] = useState(false);
  const [analisando, setAnalisando] = useState(false);
  const [analise, setAnalise] = useState(null); // {precisa_reescrever, texto_reescrito, motivo, risco}
  const msgsRef = useRef(null);

  const sugerirIA = async () => {
    setAiLoading(true);
    try {
      const ultimaMsg = mensagens[mensagens.length - 1]?.conteudo || texto || "Olá, em que posso ajudar?";
      const r = await api.post("/ai/sugerir-resposta", {
        contexto: "chat_montadora_montador",
        mensagem_usuario: ultimaMsg,
      });
      setTexto(r.data.sugestao);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiLoading(false); }
  };

  // Pares filtrados pelo Chat Jurídico (operacionais)
  const precisaFiltro = (role) => {
    const pares = new Set([
      "empresa->montadora","empresa->montador",
      "montadora->montador","montadora->empresa",
      "montador->montadora","montador->empresa",
    ]);
    return pares.has(`${user?.role}->${role}`);
  };

  const analisarMensagem = async () => {
    if (!texto.trim() || !conversaCom) return;
    if (!precisaFiltro(conversaCom.role)) { toast.info("Filtro jurídico não se aplica a este par."); return; }
    setAnalisando(true);
    try {
      const r = await api.post("/chat/analisar", {
        conteudo: texto,
        destinatario_role: conversaCom.role,
      });
      setAnalise(r.data);
      if (!r.data.precisa_reescrever) {
        toast.success("Sem risco trabalhista detectado");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAnalisando(false); }
  };

  const aplicarReescrita = () => {
    if (analise?.texto_reescrito) {
      setTexto(analise.texto_reescrito);
      setAnalise(null);
      toast.success("Texto reescrito aplicado");
    }
  };

  const loadThreads = () => api.get("/chat/minhas").then(r => setThreads(r.data));
  const loadMontadores = () => api.get("/chat/montadores-publicos").then(r => setMontadores(r.data));

  useEffect(() => {
    loadThreads();
    if (user?.role !== "montador") loadMontadores();
  }, []);

  useEffect(() => {
    const com = params.get("com");
    if (com) {
      // tenta abrir thread direto
      const m = montadores.find(x => x.user_id === com);
      if (m) setConversaCom({ user_id: com, role: "montador", nome: m.nome });
    }
  }, [params, montadores]);

  useEffect(() => {
    if (!conversaCom) return;
    api.get(`/chat/thread/${conversaCom.user_id}`).then(r => {
      setMensagens(r.data);
      setTimeout(() => msgsRef.current?.scrollTo({ top: msgsRef.current.scrollHeight }), 50);
    });
    const i = setInterval(() => {
      api.get(`/chat/thread/${conversaCom.user_id}`).then(r => setMensagens(r.data));
    }, 5000);
    return () => clearInterval(i);
  }, [conversaCom]);

  const enviar = async () => {
    if (!texto.trim() || !conversaCom) return;
    try {
      await api.post("/chat/enviar", {
        destinatario_id: conversaCom.user_id,
        destinatario_role: conversaCom.role,
        conteudo: texto,
        anexos: [],
      });
      setTexto("");
      const r = await api.get(`/chat/thread/${conversaCom.user_id}`);
      setMensagens(r.data);
      setTimeout(() => msgsRef.current?.scrollTo({ top: msgsRef.current.scrollHeight }), 50);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-6">
        <div className="label-cap">Comunicação</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Chat protegido</h1>
        <p className="text-xs text-muted-foreground mt-2">Conversas auditadas. Dados pessoais ocultos conforme perfil.</p>
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-px bg-border border border-border min-h-[600px]">
        {/* Sidebar */}
        <div className="bg-white">
          <div className="flex border-b border-border">
            <button onClick={() => setTab("conversas")} data-testid="tab-conversas"
              className={`flex-1 p-3 text-xs uppercase tracking-wider ${tab === "conversas" ? "brand-bg text-white" : "text-foreground hover:bg-secondary"}`}>
              Conversas
            </button>
            {user?.role !== "montador" && (
              <button onClick={() => setTab("montadores")} data-testid="tab-pesquisar"
                className={`flex-1 p-3 text-xs uppercase tracking-wider ${tab === "montadores" ? "brand-bg text-white" : "text-foreground hover:bg-secondary"}`}>
                Pesquisar
              </button>
            )}
          </div>
          {tab === "conversas" && (
            <div>
              {threads.length === 0 && <div className="p-6 text-xs text-muted-foreground">Sem conversas. Use "Pesquisar" para encontrar um montador.</div>}
              {threads.map((t) => (
                <button key={t.thread_id} onClick={() => setConversaCom({ user_id: t.outro_user_id, role: t.ultima.from_user_id === user?.id ? t.ultima.to_role : t.ultima.from_role, nome: t.outro_nome })}
                  className={`w-full text-left p-3 border-b border-border hover:bg-secondary ${conversaCom?.user_id === t.outro_user_id ? "bg-secondary" : ""}`}
                  data-testid={`thread-${t.thread_id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <strong className="text-sm truncate">{t.outro_nome}</strong>
                    {t.nao_lidas > 0 && <span className="bg-[#E60000] text-white text-[10px] px-1.5 rounded">{t.nao_lidas}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-1">{t.ultima.conteudo}</div>
                </button>
              ))}
            </div>
          )}
          {tab === "montadores" && (
            <div>
              {montadores.map((m) => (
                <button key={m.id} onClick={() => m.user_id && setConversaCom({ user_id: m.user_id, role: "montador", nome: m.nome })}
                  disabled={!m.user_id}
                  className="w-full text-left p-3 border-b border-border hover:bg-secondary disabled:opacity-40"
                  data-testid={`mont-pub-${m.id}`}>
                  <strong className="text-sm">{m.nome}</strong>
                  <div className="text-xs text-muted-foreground">
                    {m.cidade}/{m.estado} · Score {m.score?.toFixed?.(0) ?? "—"}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Conversa */}
        <div className="bg-white flex flex-col">
          {!conversaCom ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              Selecione uma conversa
            </div>
          ) : (
            <>
              <div className="border-b border-border p-4">
                <div className="label-cap">Conversa com</div>
                <div className="font-heading font-bold">{conversaCom.nome}</div>
              </div>
              <div ref={msgsRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#F4F4F5]" data-testid="msgs-list">
                {mensagens.map((m) => {
                  const mine = m.from_user_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] p-3 text-sm border ${mine ? "brand-bg text-white border-transparent" : "bg-white border-border"}`}>
                        <div>{m.conteudo}</div>
                        {m.foi_neutralizada && (
                          <div className={`mt-2 pt-2 border-t ${mine ? "border-white/30" : "border-border"} text-[10px] ${mine ? "text-white/80" : "text-amber-700"}`}
                            data-testid={`msg-neutralizada-${m.id}`}>
                            ⚖️ Mensagem ajustada pelo filtro jurídico
                            {user?.role === "admin" && m.conteudo_original && (
                              <div className={`mt-1 italic ${mine ? "text-white/60" : "text-muted-foreground"}`}>
                                Original: "{m.conteudo_original}"
                              </div>
                            )}
                          </div>
                        )}
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                          {new Date(m.created_at).toLocaleString("pt-BR")}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-border p-3 space-y-2">
                {precisaFiltro(conversaCom.role) && (
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-600" />
                    Chat Jurídico ativo · mensagens são revisadas para evitar linguagem trabalhista
                  </div>
                )}
                <div className="flex gap-2">
                  <Input value={texto} onChange={(e) => setTexto(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && enviar()}
                    placeholder="Digite sua mensagem"
                    data-testid="chat-input" />
                  <Button onClick={enviar} className="btn-sharp brand-bg text-white" data-testid="chat-enviar">Enviar</Button>
                </div>
                <div className="flex gap-2">
                  {precisaFiltro(conversaCom.role) && (
                    <Button onClick={analisarMensagem} variant="outline" size="sm"
                      disabled={analisando || !texto.trim()}
                      className="btn-sharp text-xs flex-1"
                      data-testid="chat-analisar-juridico">
                      {analisando ? "Revisando…" : "⚖️ Revisar antes de enviar"}
                    </Button>
                  )}
                  <Button onClick={sugerirIA} variant="outline" size="sm"
                    disabled={aiLoading}
                    className="btn-sharp text-xs flex-1"
                    data-testid="chat-ia">
                    {aiLoading ? "Pensando…" : "💡 Sugerir resposta"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Dialog de pré-análise jurídica */}
      <Dialog open={!!analise} onOpenChange={(o) => !o && setAnalise(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>⚖️ Revisão Jurídica</DialogTitle></DialogHeader>
          {analise && (
            <div className="space-y-3">
              <div className={`border-l-4 p-3 ${RISCO_COLORS[analise.risco] || RISCO_COLORS.nenhum}`}>
                <div className="font-bold uppercase text-xs">Risco trabalhista: {analise.risco}</div>
                {analise.motivo && <div className="text-xs mt-1">{analise.motivo}</div>}
              </div>
              <div>
                <div className="label-cap mb-1">Seu texto original</div>
                <div className="border border-border p-3 text-sm bg-[#F4F4F5]" data-testid="analise-original">{texto}</div>
              </div>
              {analise.precisa_reescrever && analise.texto_reescrito && analise.texto_reescrito !== texto && (
                <div>
                  <div className="label-cap mb-1 text-green-700">Sugestão neutralizada</div>
                  <div className="border border-green-500 p-3 text-sm bg-green-50" data-testid="analise-reescrito">{analise.texto_reescrito}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnalise(null)}>Cancelar</Button>
            {analise?.precisa_reescrever && analise?.texto_reescrito && analise.texto_reescrito !== texto && (
              <Button onClick={aplicarReescrita} className="brand-bg text-white" data-testid="analise-aplicar">
                Aplicar reescrita
              </Button>
            )}
            {(!analise?.precisa_reescrever || analise?.texto_reescrito === texto) && (
              <Button onClick={() => setAnalise(null)} className="brand-bg text-white">OK</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
